/**
 * Cart line message editor for APO/MWS message properties.
 * Keeps the existing line-item properties intact and lets customers
 * update "Card Message" / "Personal Message" directly from cart.
 */
(function () {
  'use strict';

  var SAVE_LABEL = 'Save message';
  var SKIP_LABEL = 'No, thanks';
  var BLANK_SAVE_PROMPT = 'No problem. If you\'d rather skip the card, tap "No, thanks".';
  var SKIP_SUCCESS_MESSAGE = 'No message needed for this arrangement.';
  var CHECKOUT_PROMPT_SINGLE = 'Before checkout, please either save your card message or tap "No, thanks" if you\'d rather leave it blank.';
  var CHECKOUT_PROMPT_MULTI = 'Before checkout, please either save a card message for each arrangement or tap "No, thanks" on the ones you\'d rather leave blank.';
  var INLINE_CHECKOUT_PROMPT = 'Please save your card message or tap "No, thanks" if you\'d like to skip it.';
  var DECLINED_STORAGE_KEY = 'wf-cart-line-message-skip:v1';

  function normalizeText(value) {
    return String(value == null ? '' : value).trim();
  }

  function decodeHtmlEntityOnce(value) {
    return String(value == null ? '' : value).replace(/&(#x?[0-9a-f]+|amp|lt|gt|quot|apos|nbsp);/gi, function (match, entity) {
      var name = String(entity || '').toLowerCase();
      if (name === 'amp') return '&';
      if (name === 'lt') return '<';
      if (name === 'gt') return '>';
      if (name === 'quot') return '"';
      if (name === 'apos') return "'";
      if (name === 'nbsp') return ' ';
      if (name.indexOf('#x') === 0) {
        var hex = parseInt(name.slice(2), 16);
        if (Number.isFinite(hex)) {
          try { return String.fromCodePoint(hex); } catch (_err) { return match; }
        }
      }
      if (name.indexOf('#') === 0) {
        var decimal = parseInt(name.slice(1), 10);
        if (Number.isFinite(decimal)) {
          try { return String.fromCodePoint(decimal); } catch (_err) { return match; }
        }
      }
      return match;
    });
  }

  function normalizeMessageText(value) {
    var current = normalizeText(value);
    for (var i = 0; i < 6; i++) {
      var next = normalizeText(decodeHtmlEntityOnce(current));
      if (next === current) break;
      current = next;
    }
    return current;
  }

  function getSessionStorage() {
    try {
      return window.sessionStorage || null;
    } catch (_err) {
      return null;
    }
  }

  function readDeclinedSelections() {
    var storage = getSessionStorage();
    if (!storage || typeof storage.getItem !== 'function') return {};
    try {
      var parsed = JSON.parse(storage.getItem(DECLINED_STORAGE_KEY) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_err) {
      return {};
    }
  }

  function writeDeclinedSelections(next) {
    var storage = getSessionStorage();
    if (!storage || typeof storage.setItem !== 'function') return;
    try {
      storage.setItem(DECLINED_STORAGE_KEY, JSON.stringify(next && typeof next === 'object' ? next : {}));
    } catch (_err) {}
  }

  function getBlockIdentifier(block) {
    if (!block || typeof block.getAttribute !== 'function') return '';
    var key = normalizeText(block.getAttribute('data-line-key') || block.getAttribute('data-line-index'));
    var slot = normalizeText(block.getAttribute('data-line-message-slot'));
    return slot ? key + ':' + slot : key;
  }

  function getBaseMessageName(block) {
    if (!block || typeof block.getAttribute !== 'function') return '';
    var explicit = normalizeText(block.getAttribute('data-line-message-base-name'));
    if (explicit) return explicit;
    return normalizeText(block.getAttribute('data-line-message-name'));
  }

  function buildSlotKey(baseName, slotIdx, totalSlots) {
    var base = normalizeText(baseName);
    if (!base) return '';
    if (!totalSlots || totalSlots <= 1) return base;
    return base + ' ' + slotIdx + ' of ' + totalSlots;
  }

  function resolveMessagePropertyName(block, fallbackName) {
    if (!block || typeof block.getAttribute !== 'function') return normalizeText(fallbackName);
    var existingName = normalizeText(block.getAttribute('data-line-message-name'));
    if (existingName) return existingName;

    var baseName = normalizeText(block.getAttribute('data-line-message-base-name')) || normalizeText(fallbackName);
    var slotIdx = Number(block.getAttribute('data-line-message-slot')) || 0;
    var totalSlots = Number(block.getAttribute('data-line-message-total-slots')) || 0;
    return buildSlotKey(baseName, slotIdx, totalSlots) || normalizeText(fallbackName);
  }

  function isSlotKeyForBase(propertyName, baseName) {
    var name = normalizeText(propertyName);
    var base = normalizeText(baseName);
    if (!name || !base) return false;
    if (name === base) return true;
    if (name.indexOf(base + ' ') !== 0) return false;
    var rest = name.substring(base.length + 1);
    return /^\d+\s+of\s+\d+$/i.test(rest);
  }

  function extractSlotIndex(propertyName, baseName) {
    var name = normalizeText(propertyName);
    var base = normalizeText(baseName);
    if (!name || !base || name.indexOf(base + ' ') !== 0) return 0;
    var match = name.substring(base.length + 1).match(/^(\d+)\s+of\s+(\d+)$/i);
    if (!match) return 0;
    return parseInt(match[1], 10) || 0;
  }

  function getStatusEl(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('[data-wf-line-message-status]')
      : null;
  }

  function getInput(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('[data-wf-line-message-input]')
      : null;
  }

  function getPropsEl(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('script[data-wf-line-message-properties]')
      : null;
  }

  function getSkipBtn(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('[data-wf-line-message-skip]')
      : null;
  }

  function getSaveBtn(block) {
    return block && typeof block.querySelector === 'function'
      ? block.querySelector('[data-wf-line-message-save]')
      : null;
  }

  function setStatus(statusEl, message, isError) {
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.style.color = isError ? '#b42318' : '';
  }

  function flashSuccess(block, message) {
    var statusEl = getStatusEl(block);
    if (!statusEl) return;
    setStatus(statusEl, message, false);
    statusEl.style.color = '#2e7d32';
    setTimeout(function () {
      setStatus(statusEl, '', false);
    }, 2500);
  }

  function clearBlockStatus(block) {
    setStatus(getStatusEl(block), '', false);
  }

  function setDeclinedState(block, next) {
    if (!block || typeof block.setAttribute !== 'function') return;
    var normalizedNext = !!next;
    if (normalizedNext) {
      block.setAttribute('data-line-message-declined', 'true');
    } else if (typeof block.removeAttribute === 'function') {
      block.removeAttribute('data-line-message-declined');
    }

    var skipBtn = getSkipBtn(block);
    if (skipBtn) {
      if (typeof skipBtn.setAttribute === 'function') {
        skipBtn.setAttribute('aria-pressed', normalizedNext ? 'true' : 'false');
      }
      if (normalizedNext) {
        if (typeof skipBtn.setAttribute === 'function') skipBtn.setAttribute('data-selected', 'true');
      } else if (typeof skipBtn.removeAttribute === 'function') {
        skipBtn.removeAttribute('data-selected');
      }
    }

    var blockId = getBlockIdentifier(block);
    if (!blockId) return;
    var declinedSelections = readDeclinedSelections();
    if (normalizedNext) declinedSelections[blockId] = true;
    else delete declinedSelections[blockId];
    writeDeclinedSelections(declinedSelections);
  }

  function isDeclined(block) {
    if (!block || typeof block.getAttribute !== 'function') return false;
    if (normalizeText(block.getAttribute('data-line-message-declined')).toLowerCase() === 'true') return true;
    var blockId = getBlockIdentifier(block);
    if (!blockId) return false;
    return !!readDeclinedSelections()[blockId];
  }

  function isEligibleBlock(block) {
    if (!block || typeof block.getAttribute !== 'function') return false;
    if (normalizeText(block.getAttribute('data-line-message-excluded')).toLowerCase() === 'true') return false;
    return !(block.style && block.style.display === 'none');
  }

  /**
   * Shopify outputs {{ item.properties | json }} as either:
   *   - object: {"key":"val",...}
   *   - array of pairs: [["key","val"],["key2","val2"],...]
   * We must handle both formats.
   */
  function parseProperties(raw) {
    if (!raw) return {};
    try {
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        var obj = {};
        for (var i = 0; i < parsed.length; i++) {
          var pair = parsed[i];
          if (Array.isArray(pair) && pair.length >= 2) {
            obj[String(pair[0])] = pair[1];
          }
        }
        return obj;
      }
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_err) {
      return {};
    }
  }

  function buildUpdatedProperties(existingProperties, propertyName, nextValue, options) {
    var props = {};
    var existing = existingProperties && typeof existingProperties === 'object' ? existingProperties : {};
    Object.keys(existing).forEach(function (key) {
      var val = existing[key];
      props[key] = val == null ? '' : String(val);
    });

    var targetName = normalizeText(propertyName);
    if (!targetName) return props;

    var value = normalizeMessageText(nextValue);
    if (value) props[targetName] = value;
    else delete props[targetName];

    var opts = options && typeof options === 'object' ? options : {};
    var baseName = normalizeText(opts.baseName);
    var totalSlots = Number(opts.totalSlots) || 0;
    if (baseName && totalSlots > 0) {
      Object.keys(props).forEach(function (key) {
        if (!isSlotKeyForBase(key, baseName)) return;
        var slot = extractSlotIndex(key, baseName);
        if (totalSlots === 1) {
          if (key !== baseName) delete props[key];
          return;
        }
        if (slot === 0 && key === baseName) {
          delete props[key];
          return;
        }
        if (slot > totalSlots) delete props[key];
      });
    }

    return props;
  }

  function getRawStoredMessageValue(block) {
    var propsEl = getPropsEl(block);
    var propertyName = normalizeText(block && typeof block.getAttribute === 'function'
      ? block.getAttribute('data-line-message-name')
      : '');
    if (!propsEl || !propertyName) return '';
    var existing = parseProperties(propsEl.textContent || '');
    return normalizeText(existing[propertyName]);
  }

  function getStoredMessageValue(block) {
    return normalizeMessageText(getRawStoredMessageValue(block));
  }

  function syncDeclinedState(block) {
    if (!block) return;
    var input = getInput(block);
    var currentValue = normalizeMessageText(input && typeof input.value === 'string' ? input.value : '');
    var storedValue = getStoredMessageValue(block);
    if (currentValue || storedValue) {
      setDeclinedState(block, false);
      return;
    }
    setDeclinedState(block, isDeclined(block));
  }

  /** Raw XHR to bypass MWS/APO's window.fetch monkey-patch. */
  function xhrPost(url, payload) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (!xhr.responseText) {
            resolve({});
            return;
          }
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (_err) {
            resolve({ raw: xhr.responseText });
          }
        } else {
          reject(new Error(xhr.responseText || ('Cart change failed: ' + xhr.status)));
        }
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(JSON.stringify(payload));
    });
  }

  /**
   * BUG-025 silent refresh: snapshot every editor's in-progress state so we can re-apply it
   * after swapping the cart section HTML. Without this, customers typing into "slot 2" while
   * "slot 1" is being saved would lose their text on every save round-trip.
   */
  function snapshotEditorState(root) {
    var snapshot = {};
    var doc = root || (typeof document !== 'undefined' ? document : null);
    if (!doc || typeof doc.querySelectorAll !== 'function') return snapshot;
    var blocks = doc.querySelectorAll('[data-wf-line-message-block]');
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      var id = getBlockIdentifier(block);
      if (!id) continue;
      var input = getInput(block);
      if (!input) continue;
      var stored = getStoredMessageValue(block);
      var storedRaw = getRawStoredMessageValue(block);
      var current = normalizeMessageText(input.value);
      snapshot[id] = {
        current: current,
        dirty: current !== stored || storedRaw !== current,
        declined: isDeclined(block),
        focused: typeof document !== 'undefined' && document.activeElement === input,
        selectionStart: typeof input.selectionStart === 'number' ? input.selectionStart : null,
        selectionEnd: typeof input.selectionEnd === 'number' ? input.selectionEnd : null,
      };
    }
    return snapshot;
  }

  function applyEditorSnapshot(root, snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return;
    var doc = root || (typeof document !== 'undefined' ? document : null);
    if (!doc || typeof doc.querySelectorAll !== 'function') return;
    var blocks = doc.querySelectorAll('[data-wf-line-message-block]');
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      var id = getBlockIdentifier(block);
      var snap = id ? snapshot[id] : null;
      if (!snap) continue;
      var input = getInput(block);
      if (!input) continue;
      if (snap.dirty) {
        input.value = snap.current || '';
        setDeclinedState(block, false);
      } else if (snap.declined) {
        setDeclinedState(block, true);
      }
      if (snap.focused && typeof input.focus === 'function') {
        try {
          input.focus();
          if (snap.selectionStart != null && snap.selectionEnd != null && typeof input.setSelectionRange === 'function') {
            input.setSelectionRange(snap.selectionStart, snap.selectionEnd);
          }
        } catch (_err) {}
      }
    }
  }

  /**
   * Swap the cart section's contents in-place using Shopify's Sections Rendering API
   * response. Falls back to no-op if the response is missing or malformed — never blanks the cart.
   *
   * HF Pipeline renders cart rows inside `[data-line-items]`, while the API
   * section returns `[data-api-content]`. Swap only that row container so the
   * delivery form and checkout controls are not duplicated.
   */
  function swapCartSection(sectionsHtml) {
    if (!sectionsHtml || typeof sectionsHtml !== 'object') return false;
    if (typeof document === 'undefined' || typeof document.createElement !== 'function') return false;

    var liveCartForm = document.getElementById('cart_form') || document.querySelector('.cart__form');
    var liveSectionRoot = null;
    if (liveCartForm && typeof liveCartForm.closest === 'function') {
      liveSectionRoot = liveCartForm.closest('[id^="shopify-section-"]');
    }
    if (!liveSectionRoot && typeof document.querySelector === 'function') {
      liveSectionRoot = document.querySelector('[data-section-type="cart"]');
    }
    if (!liveSectionRoot) liveSectionRoot = liveCartForm; // last resort
    if (!liveSectionRoot) {
      console.warn('[wf-cart-line-messages] swap aborted: no live cart form on page');
      return false;
    }

    var sectionKeys = Object.keys(sectionsHtml);
    if (sectionKeys.length === 0) return false;
    /**
     * In `shopify theme dev`, the response can contain MANY keys (preview wrappers
     * keyed by hashes, plus the actual section markup). Pick by signal, not order:
     *   1. Exact key 'api-cart-items' (the HF row refresh section).
     *   2. Any key whose value looks like a cart fragment (`[data-line-items]` / `[data-api-content]` / `id="cart_form"`).
     *   3. Longest string value (fallback — preview-key strings are tiny, ~60 chars).
     */
    var html = '';
    var pickedKey = '';
    var cartSignals = /data-line-items|data-api-content|id=["']cart_form["']|class=["'][^"']*\bcart__(form|item-list|items)\b|class=["'][^"']*\bcart-items\b/;
    if (typeof sectionsHtml['api-cart-items'] === 'string' && cartSignals.test(sectionsHtml['api-cart-items'])) {
      html = sectionsHtml['api-cart-items'];
      pickedKey = 'api-cart-items';
    } else if (typeof sectionsHtml['cart__main'] === 'string' && cartSignals.test(sectionsHtml['cart__main'])) {
      html = sectionsHtml['cart__main'];
      pickedKey = 'cart__main';
    }
    if (!html) {
      for (var i = 0; i < sectionKeys.length; i++) {
        var candidate = sectionsHtml[sectionKeys[i]];
        if (typeof candidate === 'string' && cartSignals.test(candidate)) {
          html = candidate;
          pickedKey = sectionKeys[i];
          break;
        }
      }
    }
    if (!html) {
      var longestLen = 0;
      for (var j = 0; j < sectionKeys.length; j++) {
        var v = sectionsHtml[sectionKeys[j]];
        if (typeof v === 'string' && v.length > longestLen) {
          longestLen = v.length;
          html = v;
          pickedKey = sectionKeys[j];
        }
      }
    }
    if (!html) {
      console.warn('[wf-cart-line-messages] swap aborted: empty sections payload', { sectionKeys: sectionKeys });
      return false;
    }

    var staging = null;
    if (typeof window !== 'undefined' && typeof window.DOMParser === 'function') {
      try {
        var parsed = new window.DOMParser().parseFromString('<!doctype html><html><body>' + html + '</body></html>', 'text/html');
        staging = parsed && parsed.body ? parsed.body : null;
      } catch (_err) {
        staging = null;
      }
    }
    if (!staging) {
      staging = document.createElement('div');
      staging.innerHTML = html;
    }

    var nextLineItems = staging.querySelector('[data-line-items]');
    var nextApiContent = staging.querySelector('[data-api-content]');
    var nextItemList = nextLineItems || nextApiContent || staging.querySelector('.cart__item-list') || staging.querySelector('.cart-items');
    var nextCartForm = staging.querySelector('#cart_form') || staging.querySelector('.cart__form');
    if (!nextItemList && !nextCartForm) {
      console.warn(
        '[wf-cart-line-messages] swap aborted: no cart rows in response',
        { responseKeys: sectionKeys, pickedKey: pickedKey, htmlLength: html.length, htmlHead: html.substring(0, 600), htmlTail: html.substring(Math.max(0, html.length - 300)) }
      );
      return false;
    }

    var currentItemList = liveSectionRoot.querySelector('[data-line-items]') || liveSectionRoot.querySelector('.cart__item-list') || liveSectionRoot.querySelector('.cart-items');

    var snapshot = snapshotEditorState(document);

    var swapped = false;
    var swapMode = '';
    if (nextApiContent && currentItemList) {
      currentItemList.innerHTML = nextApiContent.innerHTML;
      swapped = true;
      swapMode = 'api-content';
    } else if (nextItemList && currentItemList && currentItemList.parentNode) {
      currentItemList.parentNode.replaceChild(nextItemList, currentItemList);
      swapped = true;
      swapMode = 'item-list';
    } else if (nextCartForm && liveCartForm && liveCartForm.parentNode) {
      liveCartForm.parentNode.replaceChild(nextCartForm, liveCartForm);
      swapped = true;
      swapMode = 'cart-form';
    } else {
      console.warn('[wf-cart-line-messages] swap aborted: no compatible target', {
        hasNextItemList: !!nextItemList,
        hasCurrentItemList: !!currentItemList,
        hasNextCartForm: !!nextCartForm,
        hasLiveCartForm: !!liveCartForm,
      });
      return false;
    }

    applyEditorSnapshot(document, snapshot);
    if (typeof document.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
      try {
        document.dispatchEvent(new CustomEvent('wf:cart:refreshed', { detail: { source: 'wf-cart-line-messages', swapped: swapped } }));
      } catch (_err) {}
    }
    return true;
  }

  /**
   * BUG-025 follow-up: when ANYTHING else (e.g. the theme's quantity stepper at
   * `utilities.js → quantityBox.updateCart`) mutates the cart via /cart/change.js,
   * the existing `jsCart.updateView` only patches `.cart__total`/`.quantity-input`
   * piecemeal — it never re-renders `.cart__item-list` unless line-item COUNT
   * changes. So increasing qty 1 → 2 leaves only one message editor on screen
   * until a full page refresh. We silently re-render the cart section here using
   * the same Sections Rendering API + snapshot/restore plumbing as `saveLineMessage`.
   */
  function _shouldSilentRefreshOnCart() {
    if (typeof document === 'undefined' || typeof document.querySelector !== 'function') return false;
    return !!document.querySelector('[data-line-items]') || !!document.querySelector('.cart__form') || !!document.querySelector('[data-wf-line-message-block]');
  }

  var _silentRefreshTimer = null;
  var _silentRefreshInFlight = false;
  var _silentRefreshPending = false;

  /**
   * HF Pipeline already has a narrow cart-row section (`api-cart-items`).
   * Refresh that instead of the whole `/cart` page so delivery state stays live.
   */
  function _runSilentCartRefresh() {
    if (_silentRefreshInFlight) {
      _silentRefreshPending = true;
      return;
    }
    _silentRefreshInFlight = true;
    var rootUrl = (typeof window !== 'undefined' && window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
    var url = rootUrl + '?section_id=api-cart-items&_=' + Date.now();
    var fetchFn = (typeof window !== 'undefined' && typeof window.fetch === 'function')
      ? window.fetch.bind(window)
      : null;
    var done = function (html) {
      if (html && typeof html === 'string' && html.length > 0) {
        try { swapCartSection({ 'api-cart-items': html }); }
        catch (err) { console.warn('[wf-cart-line-messages] silent refresh swap failed', err); }
      }
      _silentRefreshInFlight = false;
      if (_silentRefreshPending) {
        _silentRefreshPending = false;
        _runSilentCartRefresh();
      }
    };
    if (fetchFn) {
      fetchFn(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then(function (res) { return res && res.ok ? res.text() : null; })
        .then(done)
        .catch(function () { done(null); });
    } else {
      try {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Accept', 'text/html');
        xhr.onload = function () {
          done(xhr.status >= 200 && xhr.status < 300 ? xhr.responseText : null);
        };
        xhr.onerror = function () { done(null); };
        xhr.send();
      } catch (_e) { done(null); }
    }
  }

  function _scheduleSilentCartRefresh() {
    if (!_shouldSilentRefreshOnCart()) return;
    if (_silentRefreshTimer) clearTimeout(_silentRefreshTimer);
    _silentRefreshTimer = setTimeout(function () {
      _silentRefreshTimer = null;
      _runSilentCartRefresh();
    }, 200);
  }

  function _isCartMutationUrl(url) {
    if (typeof url !== 'string' || !url) return false;
    return url.indexOf('/cart/change') !== -1
      || url.indexOf('/cart/update') !== -1
      || url.indexOf('/cart/add') !== -1
      || url.indexOf('/cart/clear') !== -1;
  }

  function _isOurOwnSectionedRequest(url, body) {
    if (typeof url !== 'string') return false;
    if (url.indexOf('/cart/change') === -1 && url.indexOf('/cart/update') === -1) return false;
    var bodyStr = '';
    if (typeof body === 'string') bodyStr = body;
    else if (body && typeof body === 'object') {
      try { bodyStr = JSON.stringify(body); } catch (_e) {}
    }
    return bodyStr.indexOf('"sections":"api-cart-items"') !== -1
      || bodyStr.indexOf('sections=api-cart-items') !== -1
      || bodyStr.indexOf('"sections":"cart__main"') !== -1
      || bodyStr.indexOf('sections=cart__main') !== -1;
  }

  function _installExternalCartHook() {
    if (typeof window === 'undefined') return;
    if (window.__WF_CART_MSG_EXTERNAL_HOOK__) return;
    window.__WF_CART_MSG_EXTERNAL_HOOK__ = true;
    if (window.jQuery && typeof window.jQuery === 'function') {
      try {
        window.jQuery(document).on('ajaxComplete.wfCartMessages', function (_event, xhr, settings) {
          if (!settings || !settings.url) return;
          if (!_isCartMutationUrl(settings.url)) return;
          if (_isOurOwnSectionedRequest(settings.url, settings.data)) return;
          if (xhr && xhr.status >= 200 && xhr.status < 300) _scheduleSilentCartRefresh();
        });
      } catch (_e) {}
    }
  }

  function setButtonLoadingState(block, next, mode) {
    var saveBtn = getSaveBtn(block);
    var skipBtn = getSkipBtn(block);
    if (saveBtn) {
      saveBtn.disabled = !!next;
      saveBtn.textContent = next && mode !== 'decline' ? 'Saving...' : SAVE_LABEL;
    }
    if (skipBtn) {
      skipBtn.disabled = !!next;
      skipBtn.textContent = next && mode === 'decline' ? 'Saving...' : SKIP_LABEL;
    }
  }

  // Shopify inserts a newly-added line (e.g. the delivery-fee product) at
  // position 1, which shifts every other line's 1-based index, and recalculates
  // a line's key whenever its properties change. So a cart/change.js write keyed
  // on the render-time `line` index lands on the wrong line once the fee is
  // added (the personal message ended up on the invisible fee line). Target the
  // stable line KEY instead — read from the group wrapper — and refresh it from
  // each response, because the key changes after every successful write.
  function getLineItemKey(block) {
    if (!block || typeof block.getAttribute !== 'function') return '';
    var group = typeof block.closest === 'function' ? block.closest('[data-wf-line-message-group]') : null;
    if (group && typeof group.getAttribute === 'function') {
      var groupKey = normalizeText(group.getAttribute('data-line-key'));
      if (groupKey) return groupKey;
    }
    // Fallback: strip the trailing ":<slot>" suffix off the per-slot block key.
    return normalizeText(block.getAttribute('data-line-key')).replace(/:\d+$/, '');
  }

  function freshLineKeyFromCart(cart, writtenProperties) {
    if (!cart || !Array.isArray(cart.items)) return '';
    var keys = Object.keys(writtenProperties || {});
    if (!keys.length) return '';
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var props = item && item.properties;
      if (!props || typeof props !== 'object') continue;
      var match = true;
      for (var j = 0; j < keys.length; j++) {
        if (normalizeText(props[keys[j]]) !== normalizeText(writtenProperties[keys[j]])) { match = false; break; }
      }
      if (match && item.key) return item.key;
    }
    return '';
  }

  function setLineItemKey(block, freshKey) {
    if (!block || !freshKey) return;
    var group = typeof block.closest === 'function' ? block.closest('[data-wf-line-message-group]') : null;
    if (group && typeof group.setAttribute === 'function') group.setAttribute('data-line-key', freshKey);
  }

  function saveLineMessage(block, options) {
    var opts = options || {};
    if (!block || block.getAttribute('data-saving') === 'true') return Promise.resolve(false);

    var input = getInput(block);
    var statusEl = getStatusEl(block);
    var propsEl = getPropsEl(block);
    var lineKey = getLineItemKey(block);
    var lineQuantity = Number(block.getAttribute('data-line-quantity')) || 1;
    var propertyName = normalizeText(block.getAttribute('data-line-message-name'));
    var baseName = getBaseMessageName(block);
    var totalSlots = Number(block.getAttribute('data-line-message-total-slots')) || 0;
    var mode = opts.mode === 'decline' ? 'decline' : 'save';

    if (!input || !propsEl || !lineKey || !propertyName) {
      if (!opts.silent) setStatus(statusEl, 'We could not update your message. Please refresh and try again.', true);
      return Promise.resolve(false);
    }

    var nextValue = mode === 'decline' ? '' : input.value;
    if (mode !== 'decline' && !normalizeText(nextValue)) {
      setDeclinedState(block, false);
      if (!opts.silent) setStatus(statusEl, BLANK_SAVE_PROMPT, true);
      return Promise.resolve(false);
    }

    var existingParsed = parseProperties(propsEl.textContent || '');
    var nextProperties = buildUpdatedProperties(existingParsed, propertyName, nextValue, {
      baseName: baseName,
      totalSlots: totalSlots || lineQuantity,
    });
    block.setAttribute('data-saving', 'true');
    if (!opts.silent) setButtonLoadingState(block, true, mode);

    var requestPayload = {
      id: lineKey,
      quantity: lineQuantity,
      properties: nextProperties,
    };
    var wantsSilentRefresh = !opts.silent && opts.skipSectionRefresh !== true;
    if (wantsSilentRefresh) {
      requestPayload.sections = 'api-cart-items';
      requestPayload.sections_url = '/cart';
    }

    return xhrPost('/cart/change.js', requestPayload)
      .then(function (response) {
        propsEl.textContent = JSON.stringify(nextProperties);
        // The write just changed this line's properties, recalculating its key;
        // refresh the cached key so the next save targets the new one.
        var freshLineItemKey = freshLineKeyFromCart(response, nextProperties);
        if (freshLineItemKey) setLineItemKey(block, freshLineItemKey);
        if (mode === 'decline') {
          input.value = '';
          setDeclinedState(block, true);
          if (!opts.silent) flashSuccess(block, SKIP_SUCCESS_MESSAGE);
        } else {
          input.value = normalizeMessageText(input.value);
          setDeclinedState(block, false);
          if (!opts.silent) flashSuccess(block, 'Saved');
        }
        if (wantsSilentRefresh && response && response.sections) {
          try { swapCartSection(response.sections); } catch (refreshErr) { console.warn('[wf-cart-line-messages] silent refresh failed', refreshErr); }
        }
        return true;
      })
      .catch(function (err) {
        console.error('[wf-cart-line-messages] save failed', err);
        if (!opts.silent) setStatus(statusEl, 'We could not save your message. Please try again.', true);
        return false;
      })
      .finally(function () {
        block.removeAttribute('data-saving');
        if (!opts.silent) setButtonLoadingState(block, false, mode);
      });
  }

  function declineLineMessage(block, options) {
    var opts = options || {};
    if (!block) return Promise.resolve(false);

    var input = getInput(block);
    var propsEl = getPropsEl(block);
    var propertyName = normalizeText(block.getAttribute('data-line-message-name'));
    if (!input || !propsEl || !propertyName) {
      if (!opts.silent) setStatus(getStatusEl(block), 'We could not update your message. Please refresh and try again.', true);
      return Promise.resolve(false);
    }

    var storedValue = getStoredMessageValue(block);
    input.value = '';
    clearBlockStatus(block);

    if (!storedValue) {
      propsEl.textContent = JSON.stringify(buildUpdatedProperties(parseProperties(propsEl.textContent || ''), propertyName, ''));
      setDeclinedState(block, true);
      if (!opts.silent) flashSuccess(block, SKIP_SUCCESS_MESSAGE);
      return Promise.resolve(true);
    }

    return saveLineMessage(block, {
      mode: 'decline',
      silent: !!opts.silent,
    });
  }

  /**
   * Saves all message editors that have unsaved changes.
   * Skips page reload — intended for pre-checkout auto-save.
   * Returns a Promise that resolves with the save results.
   */
  function saveAllUnsavedMessages() {
    var blocks = document.querySelectorAll('[data-wf-line-message-block]');
    var saves = [];

    blocks.forEach(function (block) {
      if (!isEligibleBlock(block)) return;

      var input = getInput(block);
      var propsEl = getPropsEl(block);
      var propertyName = normalizeText(block.getAttribute('data-line-message-name'));
      if (!input || !propsEl || !propertyName) return;

      var currentValue = normalizeMessageText(input.value);
      var existing = parseProperties(propsEl.textContent || '');
      var storedRaw = normalizeText(existing[propertyName]);
      var storedValue = normalizeMessageText(storedRaw);

      if (!currentValue) return;
      if (currentValue === storedValue && storedRaw === currentValue) return;

      saves.push(saveLineMessage(block, { skipReload: true, silent: true, skipSectionRefresh: true }));
    });

    if (saves.length === 0) return Promise.resolve([]);
    return Promise.all(saves);
  }

  function getCheckoutBlockingState() {
    var blocks = document.querySelectorAll('[data-wf-line-message-block]');
    var blockingBlocks = [];

    blocks.forEach(function (block) {
      if (!isEligibleBlock(block)) return;
      var input = getInput(block);
      var propsEl = getPropsEl(block);
      var propertyName = normalizeText(block.getAttribute('data-line-message-name'));
      if (!input || !propsEl || !propertyName) return;

      if (normalizeMessageText(input.value)) return;
      if (isDeclined(block)) return;
      blockingBlocks.push(block);
    });

    if (blockingBlocks.length === 0) {
      return { ok: true, blockingBlocks: [], message: '' };
    }

    return {
      ok: false,
      blockingBlocks: blockingBlocks,
      message: blockingBlocks.length > 1 ? CHECKOUT_PROMPT_MULTI : CHECKOUT_PROMPT_SINGLE,
    };
  }

  function promptCheckoutBlockers(state) {
    if (!state || state.ok || !state.blockingBlocks || !state.blockingBlocks.length) return;
    var firstBlock = state.blockingBlocks[0];
    setStatus(getStatusEl(firstBlock), INLINE_CHECKOUT_PROMPT, true);
    var input = getInput(firstBlock);
    if (input && typeof input.focus === 'function') input.focus();
    if (firstBlock && typeof firstBlock.scrollIntoView === 'function') {
      try {
        firstBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_err) {
        firstBlock.scrollIntoView();
      }
    }
  }

  function prepareMessagesForCheckout() {
    var blockingState = getCheckoutBlockingState();
    if (!blockingState.ok) {
      promptCheckoutBlockers(blockingState);
      return Promise.resolve(blockingState);
    }

    return saveAllUnsavedMessages().then(function (results) {
      var saveResults = Array.isArray(results) ? results : [];
      var hasFailure = saveResults.some(function (result) { return result === false; });
      if (hasFailure) {
        return {
          ok: false,
          blockingBlocks: [],
          message: 'We could not save one of your messages. Please try again.',
        };
      }
      return { ok: true, blockingBlocks: [], message: '' };
    });
  }

  function onDocumentClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var skipBtn = target.closest('[data-wf-line-message-skip]');
    if (skipBtn) {
      event.preventDefault();
      event.stopPropagation();
      declineLineMessage(skipBtn.closest('[data-wf-line-message-block]'));
      return;
    }

    var saveBtn = target.closest('[data-wf-line-message-save]');
    if (!saveBtn) return;
    event.preventDefault();
    event.stopPropagation();
    saveLineMessage(saveBtn.closest('[data-wf-line-message-block]'));
  }

  function onDocumentInput(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var input = target.closest('[data-wf-line-message-input]');
    if (!input) return;
    var block = input.closest('[data-wf-line-message-block]');
    if (!block) return;
    if (normalizeMessageText(input.value)) {
      setDeclinedState(block, false);
    }
    clearBlockStatus(block);
  }

  function onDocumentKeydown(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (event.key !== 'Enter' || (!event.metaKey && !event.ctrlKey)) return;
    var input = target.closest('[data-wf-line-message-input]');
    if (!input) return;
    event.preventDefault();
    saveLineMessage(input.closest('[data-wf-line-message-block]'));
  }

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('input', onDocumentInput);
  document.addEventListener('keydown', onDocumentKeydown);
  _installExternalCartHook();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _installExternalCartHook, { once: true });
  }

  function initMessageCards(messageCardConfig) {
    var config = messageCardConfig && typeof messageCardConfig === 'object' ? messageCardConfig : {};
    var propertyName = normalizeText(config.propertyName);
    var canInitializeEditor = Boolean(config.enabled && propertyName);

    function appendIds(target, values) {
      if (!Array.isArray(values)) return;
      values.forEach(function (id) {
        var key = String(id || '').trim();
        if (key) target.push(key);
      });
    }

    var excludedIds = Array.isArray(config.excludedProductIds)
      ? config.excludedProductIds.map(String)
      : [];
    var excludedVariantIds = Array.isArray(config.excludedVariantIds)
      ? config.excludedVariantIds.map(String)
      : [];
    var deliveryConfig = window.WF_LD_DEBUG_STATE && window.WF_LD_DEBUG_STATE.delivery;
    if (deliveryConfig && typeof deliveryConfig === 'object') {
      appendIds(excludedIds, deliveryConfig.excluded_products);
      appendIds(excludedVariantIds, deliveryConfig.excluded_variants);
    }
    var excludedSet = {};
    var excludedVariantSet = {};
    excludedIds.forEach(function (id) { excludedSet[id] = true; });
    excludedVariantIds.forEach(function (id) { excludedVariantSet[id] = true; });

    var pending = document.querySelectorAll('[data-wf-line-message-pending]');
    pending.forEach(function (block) {
      var productId = block.getAttribute('data-product-id') || '';
      var variantId = block.getAttribute('data-variant-id') || '';
      var productTitle = normalizeText(block.getAttribute('data-product-title')).toLowerCase();
      var isAddOnProduct = productTitle.indexOf('add-ons:') === 0 || productTitle.indexOf('add ons') === 0;
      if (isAddOnProduct || excludedSet[productId] || excludedVariantSet[variantId]) {
        block.setAttribute('data-line-message-excluded', 'true');
        block.removeAttribute('data-wf-line-message-pending');
        block.style.display = 'none';
        return;
      }
      if (!canInitializeEditor) return;
      var input = getInput(block);
      var hasExisting = input && normalizeText(input.value);
      if (!hasExisting) {
        var existingPropertyName = normalizeText(block.getAttribute('data-line-message-name'));
        var nextPropertyName = resolveMessagePropertyName(block, propertyName);
        block.setAttribute('data-line-message-name', nextPropertyName);
        if (!normalizeText(block.getAttribute('data-line-message-base-name'))) {
          block.setAttribute('data-line-message-base-name', propertyName);
        }
        if (!existingPropertyName) {
          var label = block.querySelector('.hf-line-message__label') || block.querySelector('.afc-line-message__label');
          if (label) label.textContent = propertyName;
        }
      }
      block.removeAttribute('data-line-message-excluded');
      block.removeAttribute('data-wf-line-message-pending');
      block.style.display = '';
      if (input && typeof input.value === 'string') {
        var normalizedInput = normalizeMessageText(input.value);
        if (input.value !== normalizedInput) input.value = normalizedInput;
      }
      syncDeclinedState(block);
    });
  }

  window.WF_CART_LINE_MESSAGES = {
    parseProperties: parseProperties,
    normalizeMessageText: normalizeMessageText,
    buildUpdatedProperties: buildUpdatedProperties,
    isSlotKeyForBase: isSlotKeyForBase,
    extractSlotIndex: extractSlotIndex,
    buildSlotKey: buildSlotKey,
    saveLineMessage: saveLineMessage,
    declineLineMessage: declineLineMessage,
    saveAllUnsavedMessages: saveAllUnsavedMessages,
    getCheckoutBlockingState: getCheckoutBlockingState,
    prepareMessagesForCheckout: prepareMessagesForCheckout,
    initMessageCards: initMessageCards,
    snapshotEditorState: snapshotEditorState,
    applyEditorSnapshot: applyEditorSnapshot,
    swapCartSection: swapCartSection,
  };
})();
